/**
 * P5's `VIEW_ICONS`/`VIEW_COLORS` wire vocabulary (`packages/functions/members/config.ts`, §7.4)
 * — the closed sets `PATCH /gates/{id}/view`'s `icon`/`color` fields accept — plus the
 * client-side rendering that turns a stored value into this shell's own primitives: an
 * `IconName` (`./paths`) and a personalization **variable** (`theme/tokens.ts`'s `--pc-*`, which
 * is per-scheme where the old flat hex record was not). **Owned by P6**, not by
 * P7's `screens/shared/access.ts` where it previously lived (relocated by T6,
 * `docs/engineering/review/03-cleanup-plan.md`; see `docs/product/prd-split-seams.md`'s web
 * ownership map) — both halves of the mapping this module owns are themselves P6-owned, so P7's
 * screens import the vocabulary from here rather than the reverse.
 */

/**
 * P5's `VIEW_ICONS` vocabulary (`packages/functions/members/config.ts`) — the closed set
 * `PATCH /gates/{id}/view`'s `icon` field accepts. Order matches the server's for a stable
 * picker layout.
 *
 * **The two lists are the same length again, and that is the change.** The server accepted twelve
 * names while this list offered ten: `side-door` and `handle` validated but had no artwork, so
 * `renderNameForViewIcon()` rendered them as `house` — a stored value the picker could not show
 * back. `side-door` is now drawn and offered; `handle`, `bollard`, `fob`, `intercom` and
 * `mailbox` left both lists, and `warehouse`, `factory` and `school` joined them. **Narrowing a
 * validated wire enum is not free** — see `implementation-notes.md` → P5 for the exposure a gate
 * already stored with a dropped name carries.
 */
export const VIEW_ICONS = [
  'barrier', 'gate', 'garage', 'door', 'side-door', 'key', 'house', 'warehouse', 'factory', 'school',
] as const
export type ViewIconValue = (typeof VIEW_ICONS)[number]

/** P5's closed wire enum: names leave the client; the colours are rendering-only. Fifteen
 *  swatches since `miami` (D185) — Porsche Miamiblau, sitting between `teal` and `cyan` because
 *  that is where its hue angle falls, not to make room for it.
 *
 *  **The hexes are no longer one ramp, and no longer quotable here.** Each name resolves to a
 *  *pair* of values — the light "Ceramic" ramp and graphite's "Lift" — so a hex written into a
 *  comment is right for at most one scheme. `prototype/design-tokens.json` holds both. */
export const VIEW_COLORS = [
  'lime', 'green', 'mint', 'teal', 'miami', 'cyan', 'blue', 'indigo', 'sky', 'purple', 'magenta', 'red',
  'orange', 'amber', 'gold',
] as const
export type ViewColorValue = (typeof VIEW_COLORS)[number]
/**
 * A swatch as the shell renders it: the full-strength value, and the ~13% wash behind a glyph.
 *
 * **CSS variables rather than hexes, because the ramp is per-scheme.** `PERSONALIZATION_HEX` used
 * to hand `theme/tokens.ts`'s one flat record straight to three screens, which built their inline
 * styles from it (`${hex}22` for the wash). That worked only while both colour schemes shared one
 * ramp. They no longer do — the light ("Ceramic") and graphite ("Lift") ramps are two different
 * sets of fifteen — and a record resolved once in JS cannot follow `[data-scheme]`, D188's whole
 * mechanism being that the attribute is stamped on `<html>` and CSS re-resolves on repaint with no
 * re-render. So the *name* travels to the DOM and the browser looks the colour up: `--pc-<name>`
 * and `--pc-<name>-wash`, emitted for both schemes by `cssVariables()`/`cssDarkVariables()`.
 *
 * Three screens paint a personalized gate (Home's tile, Gate Detail's identity chip, this
 * picker's selected glyph and dots), so the string-building lives here rather than three times
 * over: a fourth caller that types `var(--pc-…)` by hand is the drift this prevents.
 */
export function personalizationColor(color: ViewColorValue): string {
  return `var(--pc-${color})`
}

/** The washed form of `personalizationColor()` — the surface a full-strength glyph sits on. */
export function personalizationWash(color: ViewColorValue): string {
  return `var(--pc-${color}-wash)`
}

/**
 * The swatch a colour picker starts on when the gate has none stored.
 *
 * **Named rather than `VIEW_COLORS[0]`.** That expression made the default a property of the
 * list's *order*, so putting a swatch in its spectrum position silently re-defaulted every gate
 * personalized after it — which is how `lime` came to be the default in the first place, having
 * been chosen as the warm end of the ramp rather than as a default. The list stays in spectrum
 * order and the default is stated here.
 *
 * **`blue`, matching `--acc-barrier`.** Every un-personalized gate paints its type accent, and
 * since D138 provisions every Stage-1 gate as `barrier`, that accent is what a resident sees
 * before ever touching Customize — the default here must be the same name or opening the picker
 * shows a swatch that doesn't match the tile, and saving without changing color silently
 * recolors it. `barrier` and `garage` swapped accents (garage is now `cyan`), so this moved with
 * `barrier`'s hue rather than staying pinned to the `cyan` name — and it moved a second time when
 * that hue became the app bar's own underline.
 *
 * **The underline is what the two now share, and the swatch was moved onto it rather than the
 * other way round.** `--wordmark-accent` (`#3e5680` / `#6e8fc4`) is brand artwork, not a palette
 * entry, and it is not one of the fifteen — so making the default tile wear it while the picker
 * kept fifteen unchanged swatches is exactly the mismatch the paragraph above forbids. `blue` was
 * the nearest name and is still an honest one for the value; the fifteen names, the wire enum and
 * every stored `color` are untouched, and a gate that had chosen `blue` renders the deeper blue
 * without its stored value changing. **Do not repair a future mismatch here by pointing this at a
 * name whose swatch merely looks close** — the invariant is equality, and it is only checkable
 * because both sides read one pair of hexes in `prototype/design-tokens.json`.
 */
export const DEFAULT_VIEW_COLOR: ViewColorValue = 'blue'

/**
 * `gold` is the gate accent every un-personalized gate used to carry, kept as a swatch when the
 * default moved off it — a colour that leaves the product entirely cannot be chosen back. It is
 * **last** in the list on purpose: the picker is a plain flex row in an RTL shell, so the last
 * entry renders **leftmost**, which is where it was asked for.
 */

export function isViewColor(value: string): value is ViewColorValue {
  return (VIEW_COLORS as readonly string[]).includes(value)
}

/**
 * `VIEW_ICONS` values → this shell's `IconName` keys (`./paths`). Only `gate`
 * differs — named `gate-icon` there to avoid a bare `gate` key reading like the route id.
 */
const VIEW_ICON_RENDER_NAME: Readonly<Record<ViewIconValue, string>> = {
  barrier: 'barrier',
  gate: 'gate-icon',
  garage: 'garage',
  door: 'door',
  'side-door': 'side-door',
  key: 'key',
  house: 'house',
  warehouse: 'warehouse',
  factory: 'factory',
  school: 'school',
}

function isViewIcon(value: string): value is ViewIconValue {
  return (VIEW_ICONS as readonly string[]).includes(value)
}

/** The `IconName` to render for a stored `VIEW_ICONS` value, or `house` for anything unknown. */
export function renderNameForViewIcon(value: string | undefined): string {
  if (value === undefined || !isViewIcon(value)) return 'house'
  return VIEW_ICON_RENDER_NAME[value]
}
